import React from 'react';
import { Draggable } from '@hello-pangea/dnd';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { formatTime, cn, parseOrderItems, calculateOrderTotal, calculateTotalItems, formatPrice } from '../lib/utils';

const { FiClock, FiShoppingBag } = FiIcons;

const OrderCard = ({ order, index, onClick, isArchive = false }) => {
  const draggableId = (order._id || order.id).toString();
  const isDone = order.status === 'Completed' || order.status === 'Archived';
  
  const items = parseOrderItems(order.order);
  const totalAmount = calculateOrderTotal(items);
  const totalItems = calculateTotalItems(items);

  return (
    <Draggable draggableId={draggableId} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          onClick={() => onClick(order)}
          style={{ ...provided.draggableProps.style }}
          className={cn(
            "group relative p-4 mb-3 rounded-md transition-all cursor-pointer overflow-hidden",
            isArchive
              ? "bg-zinc-900/50 opacity-60 hover:opacity-100"
              : "bg-zinc-900 hover:bg-zinc-800",
            snapshot.isDragging
              ? "shadow-2xl scale-105 z-50 ring-2 ring-yellow-400"
              : "hover:shadow-lg"
          )}
        >
          {/* Header */}
          <div className="flex justify-between items-start mb-3">
            <div className="flex items-center gap-2">
              <span
                className={cn(
                  "px-2 py-1 font-black text-xs uppercase rounded-sm",
                  isDone ? "bg-zinc-800 text-zinc-400" : "bg-yellow-400 text-black"
                )}
              >
                Room {order.hall}
              </span>
            </div>
            <div
              className={cn(
                "flex items-center gap-1.5 text-xs font-bold",
                isDone ? "text-zinc-500" : "text-zinc-400"
              )}
            >
              <SafeIcon icon={FiClock} size={12} />
              <span className="font-mono">{formatTime(order.created_at)}</span>
            </div>
          </div>

          {/* Seat Info */}
          <div className="flex items-center gap-6 mb-3">
            <div>
              <span className="text-[10px] uppercase text-zinc-500 font-black block mb-0.5 tracking-wider">
                Row
              </span>
              <span
                className={cn(
                  "text-2xl font-black",
                  isDone ? "text-zinc-400" : "text-white"
                )}
              >
                {order.hall_row}
              </span>
            </div>
            <div className="w-px h-8 bg-zinc-800" />
            <div>
              <span className="text-[10px] uppercase text-zinc-500 font-black block mb-0.5 tracking-wider">
                Seat
              </span>
              <span
                className={cn(
                  "text-2xl font-black",
                  isDone ? "text-zinc-400" : "text-white"
                )}
              >
                {order.hall_seat}
              </span>
            </div>
          </div>

          {/* Products Preview */}
          <div className="mb-3 space-y-1">
            {items.slice(0, 3).map((item, idx) => (
              <div
                key={idx}
                className={cn(
                  "text-xs font-medium flex justify-between items-center",
                  isDone ? "text-zinc-500" : "text-zinc-300"
                )}
              >
                <span className="truncate flex-1">
                  {item.quantity}x {item.name}
                </span>
                <span className="ml-2 font-bold text-[10px]">
                  {formatPrice(item.totalPrice)}
                </span>
              </div>
            ))}
            {items.length > 3 && (
              <div className="text-[10px] text-zinc-600 font-bold">
                +{items.length - 3} more items
              </div>
            )}
          </div>

          {/* Summary */}
          <div className="flex items-center gap-4 mb-3 pt-2 border-t border-zinc-800">
            <div className="flex items-center gap-1.5">
              <SafeIcon
                icon={FiShoppingBag}
                size={12}
                className={isDone ? "text-zinc-600" : "text-zinc-500"}
              />
              <span
                className={cn(
                  "text-xs font-black",
                  isDone ? "text-zinc-500" : "text-zinc-400"
                )}
              >
                {totalItems} items
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <span
                className={cn(
                  "text-sm font-black",
                  isDone ? "text-zinc-500" : "text-yellow-400"
                )}
              >
                {formatPrice(totalAmount)}
              </span>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-between items-center pt-2 border-t border-zinc-800">
            <span
              className={cn(
                "text-xs font-mono font-black",
                isDone ? "text-zinc-600" : "text-yellow-400"
              )}
            >
              #{order.tilda_order_id}
            </span>
            {order.name && (
              <span className="text-[10px] text-zinc-600 uppercase font-black truncate max-w-[100px]">
                {order.name}
              </span>
            )}
          </div>
        </div>
      )}
    </Draggable>
  );
};

export default OrderCard;